import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

// Lightweight hash routing (no router library). The active tab path lives in the URL fragment as
// ASCII slugs, e.g. #/synk/forskott/installningar — the fragment never hits the server, so this
// needs no backend or reverse-proxy changes, survives reload, and drives the History API for
// Back/Forward. Each nesting level reads the segment just past its ancestors' and pushes a new slug
// on select; changing a level drops the levels below it.

export type Route = {
    segments: string[],
    navigate: (segments: string[], opts?: { replace?: boolean }) => void,
}

// The deepest path last seen under each ancestor path, so that returning to a tab returns to where
// you were in it rather than to its first sub-tab. Keyed by the ancestor path: picking `synk` after
// having been in `synk/forskott/tjanster` goes back there, while `swish` remembers its own.
//
// A session's worth of memory, deliberately not persisted: on reload the hash carries the whole
// path already, so there is nothing to restore. A remembered path that the current role cannot see
// is harmless — each level falls back to its first tab when the slug is not among its own.
const lastPathUnder = new Map<string, string[]>()

export function rememberPath(segments: string[]): void {
    for (let depth = 1; depth <= segments.length; depth++) {
        lastPathUnder.set(segments.slice(0, depth).join('/'), segments)
    }
}

/** The given path, extended to where the user last was under it. */
export function rememberedPath(segments: string[]): string[] {
    const remembered = lastPathUnder.get(segments.join('/'))
    return remembered !== undefined && remembered.length > segments.length ? remembered : segments
}

function readSegments(): string[] {
    const raw = window.location.hash.replace(/^#\/?/, '')
    return raw ? raw.split('/').filter(Boolean) : []
}

// The single source of the route (called once, near the top). Provided to the tree via RouteContext.
export function useHashRoute(): Route {
    const [segments, setSegments] = useState<string[]>(readSegments)
    // Wherever the path goes, record it against each of its ancestors — including the path the app
    // opened on, so a tab visited once is remembered even if it was never clicked.
    useEffect(() => { rememberPath(segments) }, [segments])
    useEffect(() => {
        // popstate fires on Back/Forward; hashchange on any other fragment edit. pushState/
        // replaceState (in navigate) fire neither, so navigate() updates state itself.
        const onChange = () => setSegments(readSegments())
        window.addEventListener('popstate', onChange)
        window.addEventListener('hashchange', onChange)
        return () => {
            window.removeEventListener('popstate', onChange)
            window.removeEventListener('hashchange', onChange)
        }
    }, [])
    const navigate = useCallback((segs: string[], opts?: { replace?: boolean }) => {
        const url = '#/' + segs.join('/')
        if (opts?.replace) window.history.replaceState(null, '', url)
        else window.history.pushState(null, '', url)
        setSegments(segs)
    }, [])
    // Stable identity except when the path actually changes, so route consumers don't re-render on
    // unrelated parent renders (e.g. a background refresh signal bump).
    return useMemo(() => ({ segments, navigate }), [segments, navigate])
}

// Outside a provider navigate is a no-op and the path is empty.
export const RouteContext = createContext<Route>({ segments: [], navigate: () => {} })

// A subtree's ancestor slugs, slash-joined (a primitive string, so its context identity is stable by
// value — no spurious re-renders). '' at the top level. Use childPrefix to extend it for children.
export const RoutePrefixContext = createContext<string>('')

export function childPrefix(prefix: string, slug: string): string {
    return prefix ? `${prefix}/${slug}` : slug
}

// Drive one tab level from the hash. `slugs` are this level's tab slugs, in order. Returns the active
// index (this level's slug in the path, else the first tab) and a `select` that pushes a new slug,
// dropping any deeper segments (changing a level resets the levels below).
export function useTabRoute(slugs: string[]): { prefix: string, activeIndex: number, select: (slug: string) => void } {
    const { segments, navigate } = useContext(RouteContext)
    const prefix = useContext(RoutePrefixContext)
    const depth = prefix ? prefix.split('/').length : 0
    const seg = segments[depth]
    const found = seg !== undefined ? slugs.indexOf(seg) : -1
    const activeIndex = found >= 0 ? found : 0
    const prefixSegs = useMemo(() => (prefix ? prefix.split('/') : []), [prefix])
    // Picking a tab returns to where you were inside it, rather than to its first sub-tab.
    const select = useCallback(
        (slug: string) => navigate(rememberedPath([...prefixSegs, slug])), [navigate, prefixSegs])
    return { prefix, activeIndex, select }
}
